import React, { useState } from "react";
import { Modal, Button, Spin } from "antd";
import {
  StopOutlined,
  LoadingOutlined,
  ExclamationCircleFilled,
  CalendarOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import * as SubscriptionService from "../../../services/InstallerSubscriptionService";

const CancelSubscriptionModal = ({ visible, onClose, subscription, onSuccess }) => {
  const dispatch = useDispatch();
  const [canceling, setCanceling] = useState(false);

  if (!subscription) return null;

  const validUntilDate =
    subscription.renews_at || subscription.ends_at || "the end of current billing cycle";

  const handleConfirmCancel = () => {
    setCanceling(true);
    dispatch(SubscriptionService.cancelInstallerSubscription(subscription.id))
      .then((res) => {
        setCanceling(false);
        onClose();
        Swal.fire({
          icon: "success",
          title: "Subscription Canceled",
          text:
            res?.message ||
            `This subscription auto-renewal is canceled. Your ${subscription.seats} seats remain active until ${res?.ends_at || validUntilDate}.`,
          confirmButtonColor: "#0284c7",
        });
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        setCanceling(false);
        Swal.fire({
          icon: "error",
          title: "Cancellation Failed",
          text:
            err?.response?.data?.message || "Could not cancel subscription. Please try again.",
        });
      });
  };

  return (
    <Modal
      open={visible}
      onCancel={canceling ? undefined : onClose}
      footer={null}
      centered
      width={460}
      className="responsive-sub-modal"
      closable={!canceling}
      destroyOnClose
    >
      <div style={{ textAlign: "center", padding: "12px 4px 4px 4px" }}>
        {/* Warning Icon Badge */}
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "16px",
            background: "#fef2f2",
            color: "#dc2626",
            border: "1.5px solid #fecaca",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "24px",
            margin: "0 auto 14px auto",
            boxShadow: "0 4px 14px rgba(220, 38, 38, 0.15)",
          }}
        >
          <StopOutlined />
        </div>

        {/* Title */}
        <h3
          style={{
            fontSize: "19px",
            fontWeight: 600,
            color: "#0f172a",
            marginBottom: "8px",
            letterSpacing: "-0.2px",
          }}
        >
          Cancel Auto-Renewal?
        </h3>

        <p
          style={{
            fontSize: "13.5px",
            color: "#64748b",
            lineHeight: 1.5,
            marginBottom: "16px",
            padding: "0 4px",
          }}
        >
          This will turn off automatic monthly billing for <strong>Subscription ID: #{subscription.id}</strong> (<strong>{subscription.seats} {subscription.seats === 1 ? "Seat" : "Seats"}</strong>).
        </p>

        {/* Retention Notice Box */}
        <div
          style={{
            background: "#fff7ed",
            border: "1px solid #fed7aa",
            borderRadius: "12px",
            padding: "12px 14px",
            marginBottom: "20px",
            textAlign: "left",
            display: "flex",
            gap: "10px",
            alignItems: "flex-start",
          }}
        >
          <CalendarOutlined style={{ color: "#ea580c", fontSize: "16px", marginTop: "2px" }} />
          <div>
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#9a3412", display: "block" }}>
              Seats Remain Active
            </span>
            <span style={{ fontSize: "12.5px", color: "#7c2d12" }}>
              Your installer team can continue using these seats until <strong>{validUntilDate}</strong> without interruption.
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="modal-actions-responsive">
          <Button
            onClick={onClose}
            disabled={canceling}
            style={{
              flex: 1,
              height: "42px",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "13.5px",
              borderColor: "#cbd5e1",
              color: "#475569",
            }}
          >
            Keep Subscription
          </Button>

          <Button
            danger
            type="primary"
            icon={canceling ? <LoadingOutlined /> : <StopOutlined />}
            onClick={handleConfirmCancel}
            disabled={canceling}
            style={{
              flex: 1.3,
              height: "42px",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "13.5px",
              background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
              borderColor: "transparent",
              boxShadow: "0 3px 10px rgba(220, 38, 38, 0.3)",
            }}
          >
            {canceling ? "Canceling..." : "Yes, Cancel Auto-Pay"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CancelSubscriptionModal;
